import React from 'react';
import { Lock, EyeOff } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { AppPermission, AppRole } from '../../types/permissions';

interface ProtectedFieldProps {
  permission?: AppPermission;
  role?: AppRole;
  anyOf?: AppPermission[];
  allOf?: AppPermission[];
  hideMode?: 'remove' | 'mask' | 'disable';
  maskText?: string;
  className?: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Componente para proteção granular e segurança a nível de campo (Field-Level Security).
 * Permite ocultar, mascarar com '••••••' ou desabilitar campos para usuários sem a permissão necessária.
 */
export const ProtectedField: React.FC<ProtectedFieldProps> = ({
  permission,
  role,
  anyOf,
  allOf,
  hideMode = 'remove',
  maskText = '••••••',
  className = '',
  children,
  fallback = null
}) => {
  const { hasPermission, hasRole, hasAnyPermission, hasAllPermissions } = usePermissions();

  let authorized = true;

  if (permission && !hasPermission(permission)) {
    authorized = false;
  }
  if (role && !hasRole(role)) {
    authorized = false;
  }
  if (anyOf && !hasAnyPermission(anyOf)) {
    authorized = false;
  }
  if (allOf && !hasAllPermissions(allOf)) {
    authorized = false;
  }

  if (authorized) {
    return <>{children}</>;
  }

  if (fallback !== null) {
    return <>{fallback}</>;
  }

  if (hideMode === 'remove') {
    return null;
  }

  if (hideMode === 'mask') {
    return (
      <span
        title="Informação restrita por permissão de acesso"
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-mono bg-slate-100 text-slate-400 border border-slate-200 select-none ${className}`}
      >
        <Lock className="w-3 h-3 text-slate-400" />
        <span>{maskText}</span>
      </span>
    );
  }

  if (hideMode === 'disable') {
    return (
      <div className={`relative group opacity-50 cursor-not-allowed pointer-events-none ${className}`}>
        {children}
        <div className="absolute inset-0 bg-slate-500/10 rounded flex items-center justify-center">
          <Lock className="w-4 h-4 text-slate-500 drop-shadow" />
        </div>
      </div>
    );
  }

  return null;
};

interface MaskedValueProps {
  value: string | number | null | undefined;
  permission?: AppPermission;
  isAllowed?: boolean;
  mask?: string;
  type?: 'currency' | 'document' | 'bank' | 'generic';
  formatter?: (val: any) => string;
  className?: string;
}

/**
 * Componente que exibe um valor monetário ou confidencial (ex: repasse, margem, CPF, chave PIX)
 * ou mascara automaticamente com ícone de cadeado caso o usuário não possua permissão mínima.
 */
export const MaskedValue: React.FC<MaskedValueProps> = ({
  value,
  permission,
  isAllowed,
  mask = '••••••',
  type = 'currency',
  formatter,
  className = ''
}) => {
  const { hasPermission } = usePermissions();
  const authorized = isAllowed !== undefined ? isAllowed : (permission ? hasPermission(permission) : true);

  if (value === null || value === undefined || value === '') {
    return <span className="text-slate-400">-</span>;
  }

  if (authorized) {
    if (formatter) {
      return <span className={className}>{formatter(value)}</span>;
    }

    if (type === 'currency') {
      if (typeof value === 'number') {
        return (
          <span className={className}>
            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)}
          </span>
        );
      }

      const strVal = String(value).trim();
      if (strVal.startsWith('R$') || strVal.startsWith('$')) {
        return <span className={className}>{strVal}</span>;
      }

      const cleaned = strVal.replace(/[^\d,-]/g, '').replace(',', '.');
      const num = parseFloat(cleaned);
      const finalNum = isNaN(num) ? 0 : num;
      return (
        <span className={className}>
          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(finalNum)}
        </span>
      );
    }

    return <span className={className}>{String(value)}</span>;
  }

  // Se não autorizado, exibe valor mascarado elegante
  return (
    <span
      title="Você não tem permissão para visualizar este valor"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-400 text-xs font-mono select-none"
    >
      <EyeOff className="w-3 h-3 text-slate-400" />
      <span>{mask}</span>
    </span>
  );
};
